// =============================================================================
// components/call/AddParticipantsSheet.tsx — WhatsApp-style "add participants".
//
// A glass bottom sheet opened from VoiceRoomScreen's add-participants entry
// point. It lists the current user's friends + followed users, supports live
// search + multi-select, and invites the selected people to the current room via
//   POST /voice/rooms/:roomId/invite  { userIds }
// The backend sends each invitee a socket ring + push that deep-links them into
// the room; they still JOIN through the normal /voice/rooms/:id/join flow, so no
// token is minted or leaked here — this sheet only fires the "come join" signal.
//
// Sources merged (deduped by id):
//   • GET /users/me/friends?status=ACCEPTED  → friendship.friend
//   • GET /users/:myUsername/following       → data.users
// Fail-soft: if a source errors it's skipped; the sheet still shows the other.
// =============================================================================
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  Pressable,
  FlatList,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import CachedImage from '../ui/CachedImage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '../ui/Icon';
import { api, getMe } from '../../services/api';
import { colors, spacing, radius, fonts } from '../../constants/theme';

interface Candidate {
  id: string;
  username?: string;
  displayName?: string;
  avatar?: string;
}

interface Props {
  visible: boolean;
  roomId: string;
  onClose: () => void;
  /** Optional: called with the count actually invited (for a toast in the host). */
  onInvited?: (count: number) => void;
}

/** Merge friends + following into a single deduped candidate list. */
async function loadCandidates(): Promise<Candidate[]> {
  const byId = new Map<string, Candidate>();

  // Friends (ACCEPTED). No username needed for this endpoint.
  try {
    const res = await api<any>('/users/me/friends?status=ACCEPTED&limit=100&type=all');
    const list: any[] = res.success ? (res.data?.friendships ?? []) : [];
    for (const f of list) {
      const u = f?.friend;
      if (u?.id && !byId.has(u.id)) {
        byId.set(u.id, { id: u.id, username: u.username, displayName: u.displayName, avatar: u.avatar });
      }
    }
  } catch { /* skip friends source on failure */ }

  // Following — needs my own username.
  try {
    const me = await getMe();
    const meUser: any = (me as any)?.data?.user ?? (me as any)?.data ?? null;
    const myUsername: string | undefined = meUser?.username;
    if (myUsername) {
      const res = await api<any>(`/users/${encodeURIComponent(myUsername)}/following?limit=100`);
      const users: any[] = res.success ? (res.data?.users ?? []) : [];
      for (const u of users) {
        if (u?.id && !byId.has(u.id)) {
          byId.set(u.id, { id: u.id, username: u.username, displayName: u.displayName, avatar: u.avatar });
        }
      }
    }
  } catch { /* skip following source on failure */ }

  return Array.from(byId.values()).sort((a, b) =>
    (a.displayName || a.username || '').localeCompare(b.displayName || b.username || ''),
  );
}

export default function AddParticipantsSheet({ visible, roomId, onClose, onInvited }: Props) {
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the candidate list each time the sheet opens (keeps it fresh + resets
  // selection/search so a reopen never carries stale state).
  useEffect(() => {
    if (!visible) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setSelected(new Set());
    setQuery('');
    loadCandidates()
      .then((list) => { if (alive) setCandidates(list); })
      .catch(() => { if (alive) setError('Could not load your people'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [visible]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      (c.displayName || '').toLowerCase().includes(q) ||
      (c.username || '').toLowerCase().includes(q),
    );
  }, [candidates, query]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleInvite = useCallback(async () => {
    const userIds = Array.from(selected);
    if (!userIds.length || inviting) return;
    setInviting(true);
    setError(null);
    try {
      const res = await api<any>(`/voice/rooms/${encodeURIComponent(roomId)}/invite`, {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      });
      if (res.success) {
        onInvited?.(res.data?.count ?? userIds.length);
        onClose();
      } else {
        setError(res.error || 'Could not send invites');
      }
    } catch (e: any) {
      setError(e?.message || 'Could not send invites');
    } finally {
      setInviting(false);
    }
  }, [selected, inviting, roomId, onInvited, onClose]);

  const renderItem = useCallback(({ item }: { item: Candidate }) => {
    const isSelected = selected.has(item.id);
    const name = item.displayName || item.username || 'User';
    return (
      <Pressable
        style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
        onPress={() => toggle(item.id)}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: isSelected }}
        accessibilityLabel={`${isSelected ? 'Deselect' : 'Select'} ${name}`}
      >
        {item.avatar ? (
          <CachedImage source={{ uri: item.avatar }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatar, styles.avatarFallback]}>
            <Text style={styles.avatarLetter}>{name.charAt(0).toUpperCase()}</Text>
          </View>
        )}
        <View style={styles.rowText}>
          <Text style={styles.rowName} numberOfLines={1}>{name}</Text>
          {!!item.username && (
            <Text style={styles.rowHandle} numberOfLines={1}>@{item.username}</Text>
          )}
        </View>
        <View style={[styles.checkbox, isSelected && styles.checkboxOn]}>
          {isSelected && <Ionicons name="checkmark" size={16} color={colors.white} />}
        </View>
      </Pressable>
    );
  }, [selected, toggle]);

  const selectedCount = selected.size;

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel="Close add participants">
        <Pressable
          style={[styles.sheet, { paddingBottom: Math.max(insets.bottom, spacing.lg) }]}
          onPress={(e) => e.stopPropagation()}
        >
          <View style={styles.grabber} />
          <Text style={styles.title}>Add participants</Text>

          {/* Search */}
          <View style={styles.searchWrap}>
            <Ionicons name="search" size={18} color={colors.faint} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search people"
              placeholderTextColor={colors.faint}
              value={query}
              onChangeText={setQuery}
              autoCapitalize="none"
              autoCorrect={false}
              accessibilityLabel="Search people"
            />
            {!!query && (
              <Pressable onPress={() => setQuery('')} hitSlop={8} accessibilityLabel="Clear search">
                <Ionicons name="close-circle" size={18} color={colors.faint} />
              </Pressable>
            )}
          </View>

          {loading ? (
            <View style={styles.center}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={styles.centerText}>Loading your people…</Text>
            </View>
          ) : (
            <FlatList
              data={filtered}
              keyExtractor={(item) => item.id}
              renderItem={renderItem}
              style={styles.list}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              ListEmptyComponent={
                <View style={styles.center}>
                  <Ionicons name="people-outline" size={32} color={colors.faint} />
                  <Text style={styles.centerText}>
                    {query ? 'No matches' : 'No friends or following yet'}
                  </Text>
                </View>
              }
            />
          )}

          {!!error && <Text style={styles.errorText}>{error}</Text>}

          {/* Invite CTA */}
          <Pressable
            style={[styles.inviteBtn, (!selectedCount || inviting) && styles.inviteBtnDisabled]}
            onPress={handleInvite}
            disabled={!selectedCount || inviting}
            accessibilityRole="button"
            accessibilityLabel={selectedCount ? `Invite ${selectedCount} people` : 'Invite'}
            accessibilityState={{ disabled: !selectedCount || inviting }}
          >
            {inviting ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <>
                <Ionicons name="person-add" size={18} color={colors.white} />
                <Text style={styles.inviteBtnText}>
                  {selectedCount ? `Invite ${selectedCount}` : 'Invite'}
                </Text>
              </>
            )}
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'flex-end',
  },
  // Glass sheet (matches the room's dark surface + hairline border language).
  sheet: {
    maxHeight: '85%',
    backgroundColor: 'rgba(26,26,46,0.98)',
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  grabber: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.25)',
    marginBottom: spacing.md,
  },
  title: {
    fontSize: 17,
    color: colors.white,
    textAlign: 'center',
    marginBottom: spacing.md,
    ...fonts.bold,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: colors.white,
    padding: 0,
    ...fonts.regular,
  },
  list: {
    marginTop: spacing.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
  },
  rowPressed: {
    opacity: 0.6,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary + '30',
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    fontSize: 18,
    color: colors.primary,
    ...fonts.bold,
  },
  rowText: {
    flex: 1,
  },
  rowName: {
    fontSize: 15,
    color: colors.white,
    ...fonts.semibold,
  },
  rowHandle: {
    fontSize: 12,
    color: colors.faint,
    marginTop: 1,
    ...fonts.regular,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  center: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xxxl,
    gap: spacing.sm,
  },
  centerText: {
    fontSize: 14,
    color: colors.faint,
    ...fonts.medium,
  },
  errorText: {
    fontSize: 13,
    color: colors.error,
    textAlign: 'center',
    marginTop: spacing.sm,
    ...fonts.medium,
  },
  inviteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    paddingVertical: spacing.md,
    marginTop: spacing.md,
  },
  inviteBtnDisabled: {
    opacity: 0.5,
  },
  inviteBtnText: {
    fontSize: 15,
    color: colors.white,
    ...fonts.bold,
  },
});
